import { Entity, ThemeExposure, ThemeSchemaResponse } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorBoundaryWrapper } from '~/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { useThemeCompanies } from '~/hooks/use-knowledge-graph/useKnowledgeGraph';
import { ThemeLogoIcon } from '~/views/theme-overview/components/theme-logo-icon/ThemeLogoIcon';
import { ThemePerformanceSection } from '~/views/theme-overview/theme-overview-drawer/components/theme-performance-section/ThemePerformanceSection';

import { StepperSection } from '../components/stepper-section/StepperSection';
import { TopCompaniesSection } from './components/top-companies-section/TopCompaniesSection';
import * as S from './ThemeOverviewTab.styles';

const TOP_COMPANIES_LIMIT = 10;

interface ThemeOverviewTabProps {
  entity?: Entity;
  theme: ThemeExposure | { theme: ThemeSchemaResponse };
  title?: string;
  isPerfDrawer?: boolean;
}

export const ThemeOverviewTab = ({
  entity,
  theme,
  title,
  isPerfDrawer = false,
}: ThemeOverviewTabProps) => {
  const { t } = useTranslation(['widget']);
  const {
    data = [],
    isFetching,
    isError,
    error,
  } = useThemeCompanies({ themeId: theme.theme.id });

  if (isFetching) {
    return <S.LoadingSkeleton />;
  }

  if (isError) {
    throw new Error(error.message);
  }

  const entityTags = data
    .map(item => item.company)
    .slice(0, TOP_COMPANIES_LIMIT);
  const slicedData = data.slice(0, TOP_COMPANIES_LIMIT);
  const remainingCount = Math.max(0, data.length - TOP_COMPANIES_LIMIT);

  return (
    <S.Root>
      {isPerfDrawer ? (
        <>
          <S.DrawerHeader>
            <ThemeLogoIcon
              logo_url={theme.theme.logo_url}
              name={theme.theme.name}
            />
            <span>
              <S.TitleText>{title}</S.TitleText>
              <S.SubtitleSmall>
                {t(
                  'assetOverview.knowledgeGraph.themeExposure.investmentTheme'
                )}
              </S.SubtitleSmall>
            </span>
          </S.DrawerHeader>
          <S.BodySmall>{theme.theme.description}</S.BodySmall>
        </>
      ) : (
        <StepperSection
          entity={entity}
          theme={theme as ThemeExposure}
          title={title}
          activeIndex={1}
          description={theme.theme.description}
        />
      )}
      <ErrorBoundaryWrapper>
        <ThemePerformanceSection
          entityTags={entityTags}
          themeId={theme.theme.id}
        />
      </ErrorBoundaryWrapper>
      {!!slicedData.length && (
        <>
          <S.HorizontalDivider />
          <TopCompaniesSection
            companies={slicedData}
            remainingCount={remainingCount}
          />
        </>
      )}
    </S.Root>
  );
};
